import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const { workspace, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [statusMsg, setStatusMsg] = useState(null);

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])); }, []);

  // poll status if redirected back from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setStatusMsg("Verifying payment...");
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatusMsg("Payment successful — workspace upgraded.");
          await refresh();
          toast.success("Plan upgraded");
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") { setStatusMsg("Checkout expired."); return; }
        if (attempts >= 6) { setStatusMsg("Still processing — refresh shortly."); return; }
        if (!cancelled) setTimeout(poll, 2000);
      } catch { setStatusMsg("Could not verify payment."); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error("Failed to start checkout"); }
    setBusy(null);
  };

  const currentPlan = workspace?.plan || "starter";

  return (
    <DashboardLayout eyebrow="Billing" title="Subscription & plans">
      {statusMsg && (
        <div className="mb-5 px-4 py-3 border border-signal-500/40 bg-signal-500/10 rounded-sm text-sm flex items-center gap-2" data-testid="billing-status-banner">
          <CheckCircle2 className="w-4 h-4 text-signal-500" />{statusMsg}
        </div>
      )}

      <div className="mb-8 data-card">
        <div className="label-eyebrow mb-1">Current plan</div>
        <div className="flex items-end gap-4">
          <div className="text-3xl font-display font-semibold capitalize">{currentPlan}</div>
          <div className="text-xs text-muted-foreground mb-1.5">{workspace?.plan_status}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 grid-borders">
        {plans.map((p) => (
          <div key={p.id} className={`p-7 flex flex-col ${currentPlan === p.id ? "bg-card relative" : ""}`} data-testid={`billing-plan-${p.id}`}>
            {currentPlan === p.id && <div className="absolute top-3 right-3 text-[0.6rem] tracking-widest uppercase text-signal-500">Active</div>}
            <div className="label-eyebrow">{p.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-display font-semibold tracking-tightest">${p.amount}</span>
              <span className="text-xs text-muted-foreground">/ mo</span>
            </div>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 mt-1 text-signal-500 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button onClick={() => checkout(p.id)} disabled={busy === p.id || currentPlan === p.id}
              data-testid={`billing-checkout-${p.id}`}
              className="mt-7 w-full rounded-sm">
              {currentPlan === p.id ? "Current plan" : busy === p.id ? "Redirecting..." : "Upgrade"}
            </Button>
          </div>
        ))}
      </div>

      <p className="mt-6 text-xs text-muted-foreground">Stripe test mode is active — use card <span className="font-mono">4242 4242 4242 4242</span> with any future date / CVC.</p>
    </DashboardLayout>
  );
}
